import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { Option } from 'src/interfaces/option.interface';
import { MobileService } from 'src/services/mobile.service';
import { OptionGroup } from '../../../../interfaces/option-group.interface';
import { OptionStack } from '@modules/menu/components';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { AccessibilityService } from '@modules/menu/services';

@Component({
  selector: 'app-option-card',
  templateUrl: './option-card.component.html',
  styleUrls: ['./option-card.component.scss'],
  animations: [
    trigger('growInOut', [
      state('false', style({ height: '0', overflow: 'hidden', opacity: '0' })),
      state('true', style({ height: '*', opacity: '1' })),
      transition('false => true', animate('400ms 200ms ease')),
      transition('true => false', animate('400ms ease')),
    ]),
  ],
})
export class OptionCardComponent implements OnInit, OnChanges {
  @Input() option: Option;
  @Input() optionGroup: OptionGroup;
  @Input() isValid: boolean;
  @Input() isModifierFlow: boolean;
  @Input() currencyCode: string;
  @Input() showPricing: boolean;
  @Input() selectedOptionIndex: number;
  @Input() maxChoiceQuantity: number;
  @Input() selectedOptions: Option[];
  @Input() hitMaxGroupQuantity: boolean;
  @Input() allowsOptionQuantity: boolean;

  @Output() clickedOption = new EventEmitter<{ option: Option; quantity: number }>();
  @Output() clickedSubOption = new EventEmitter<{ option: Option; quantity: number }>();
  @Input() isLoading = false;

  optionSubGroup = null;
  showSubgroup = false;
  quantity = 1;

  optionId = `option-${Math.random().toString(36).substring(2, 9)}`;
  descriptionId = `desc-${Math.random().toString(36).substring(2, 9)}`;

  constructor(
    public mobile: MobileService,
    private a11yService: AccessibilityService,
    private el: ElementRef
  ) {}

  ngOnInit() {
    if (this.optionGroup.minChoiceQuantity) {
      this.quantity = this.optionGroup.minChoiceQuantity;
    }
    if (this.option.quantity) {
      this.quantity = this.option.quantity;
    }
    if (this.option.isDefault) {
      this.optionClicked(this.option);
    }

    this.a11yService.focusElementId$.subscribe(id => {
      if (id === this.option.optionID) {
        this.el.nativeElement.querySelector(`#${this.option.optionID}`).focus();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // if option is not previously selected and is now selected, set quantity to 1
    if (changes.selectedOptions && changes.selectedOptions.currentValue) {
      if (
        !changes.selectedOptions.previousValue ||
        changes.selectedOptions.previousValue.length !== changes.selectedOptions.currentValue.length
      ) {
        if (
          changes.selectedOptions.currentValue?.find((option: Option) => option.optionID === this.option.optionID) &&
          !changes.selectedOptions.previousValue?.find((option: Option) => option.optionID === this.option.optionID) &&
          !this.optionGroup.mandatorySelection
        ) {
          this.option.quantity < this.optionGroup.minChoiceQuantity ? (this.quantity = this.optionGroup.minChoiceQuantity) : null;
        }
      }
    }
    if (changes.selectedOptions && changes.selectedOptions.currentValue && this.optionGroup.mandatorySelection) {
      this.quantity = this.option.quantity;
    }
  }

  optionClicked(option: Option) {
    if (this.hitMaxGroupQuantity && this.allowsOptionQuantity && !option.isSelected) {
      this.a11yService.announce(`Maximum selections reached for ${this.optionGroup.name}. Remove an option to select a different one.`);
      return;
    }

    if (this.quantity > this.maxChoiceQuantity) {
      this.quantity = this.maxChoiceQuantity;
    }
    if (this.quantity < this.optionGroup.minChoiceQuantity) {
      this.quantity = this.optionGroup.minChoiceQuantity;
    }

    const wasSelected = option.isSelected;
    this.clickedOption.emit({ option, quantity: this.quantity });

    // Announce option selection/deselection to screen readers after a slight delay
    // to ensure state has updated
    setTimeout(() => {
      // Only announce if state actually changed
      if (wasSelected !== option.isSelected) {
        this.a11yService.announceOptionSelection(option, option.isSelected, this.optionGroup);
      }
    }, 100);
  }

  subOptionClicked(option: Option, quantity = 1) {
    this.clickedSubOption.emit({ option, quantity });

    // Announce sub-option selection
    setTimeout(() => {
      this.a11yService.announceOptionSelection(option, option.isSelected, this.optionGroup);
    }, 100);
  }

  addOrRemoveOptionAndUpdateView(optionStack: OptionStack) {
    const multiselect = optionStack.optionGroup.maxAllowed !== 1;
    if (!multiselect) {
      optionStack.optionGroup.options.forEach((selectedOption: Option, i: number) => {
        const oldValue = this.optionGroup.options.findIndex((option: Option) => option.optionID === selectedOption.optionID);
        if (oldValue >= 0) {
          this.optionGroup.options.splice(oldValue, 1);
        }
      });
      this.addOrRemoveSelectedOption(optionStack.option);
    } else {
      const groupLimit =
        optionStack.optionGroup.maxAllowed !== 0 ? optionStack.optionGroup.maxAllowed === 0 : optionStack.optionGroup.options.length;
      this.addOrRemoveSelectedOption(optionStack.option);
    }
  }

  addOrRemoveSelectedOption(option: Option) {
    if (this.optionGroup.options.find((opt: Option) => opt.optionID === option.optionID)) {
      this.removeSelectedOption(option);
    } else {
      this.optionGroup.options.push(option);
    }
  }

  removeSelectedOption(option: Option) {
    const index = this.optionGroup.options.findIndex((opt: Option) => opt.optionID === option.optionID);
    if (index > -1) {
      this.optionGroup.options.splice(index, 1);
    }
  }

  animationStart() {
    // if (this.option.isSelected) {
    //   this.showSubgroup = true;
    // }
  }

  animationDone() {
    // if (!this.option.isSelected) {
    //   this.showSubgroup = false;
    // }
  }

  increaseQuantity(event: Event) {
    event?.stopPropagation();
    const prevQuantity = this.quantity;
    if (this.quantity < this.maxChoiceQuantity) {
      this.quantity++;
    }
    this.updateOptionQuantity();

    // Announce quantity change
    if (prevQuantity !== this.quantity) {
      this.a11yService.announceQuantityChange(this.option, this.quantity, prevQuantity);
    }
  }

  decreaseQuantity(event: Event) {
    event?.stopPropagation();
    const prevQuantity = this.quantity;
    if (this.quantity > 1) {
      this.quantity--;
    }
    this.updateOptionQuantity();

    // Announce quantity change
    if (prevQuantity !== this.quantity) {
      this.a11yService.announceQuantityChange(this.option, this.quantity, prevQuantity);
    }
  }

  updateOptionQuantity() {
    this.option.quantity = this.quantity;
  }

  getAriaAttributes(): { [key: string]: string } {
    return this.a11yService.getOptionAriaAttributes(this.option, this.optionGroup);
  }

  formatCurrency(amountCents: number, currencyCode?: string): string {
    if (!currencyCode || currencyCode === 'unknown') {
      return `${(amountCents / 100).toFixed(2)}`; // Fallback to plain number
    }

    try {
      const amount = amountCents / 100;
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(amount);
    } catch {
      return `${(amountCents / 100).toFixed(2)} ${currencyCode}`;
    }
  }
}
